import { EyeOutlined } from '@ant-design/icons';
import { type IGiftRegistryAdminSummary } from '@mahawi/eshop-common/src/types';
import { Button, Space, Tag, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import GiftRegistryDetailDrawer from '../components/gift-registry/gift-registry-detail-drawer';
import TableComponent from '../components/table';
import UpdatedAt from '../components/updated-at';
import { EDateFormat } from '../fragments/date-format';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { giftRegistriesLoad } from '../reducers/gift-registry/reducer';
import { SPACING } from '../theme/tokens';

type TGiftRegistryRow = IGiftRegistryAdminSummary & { key: string };

function GiftRegistriesContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const registries = useSelector((s: RootState) => s.GiftRegistry.registries);
  const inProcess = useSelector((s: RootState) => s.GiftRegistry.inProcess);
  const updatedAt = useSelector((s: RootState) => s.GiftRegistry.updatedAt);

  const [selectedRegistryUUID, setSelectedRegistryUUID] = useState<
    string | null
  >(null);

  useEffect(() => {
    dispatch(giftRegistriesLoad());
  }, [dispatch]);

  const dataSource = useMemo<TGiftRegistryRow[]>(
    () =>
      (registries ?? []).map((registry) => ({
        key: registry.uuid,
        ...registry,
      })),
    [registries],
  );

  const columns = useMemo<ColumnsType<TGiftRegistryRow>>(
    () => [
      {
        title: 'Title',
        dataIndex: 'title',
        sorter: (a: TGiftRegistryRow, b: TGiftRegistryRow): number =>
          a.title.localeCompare(b.title),
      },
      {
        title: 'Owner',
        render: (
          _text: unknown,
          record: TGiftRegistryRow,
        ): React.ReactElement => (
          <>
            <Typography.Text>{record.ownerDisplayName}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {record.ownerAccountEmail}
            </Typography.Text>
          </>
        ),
      },
      {
        title: 'Visibility',
        dataIndex: 'isPublic',
        render: (isPublic: boolean): React.ReactElement => (
          <Tag color={isPublic ? 'green' : 'default'}>
            {isPublic ? 'Public' : 'Private'}
          </Tag>
        ),
      },
      {
        title: 'Country/Currency',
        render: (_text: unknown, record: TGiftRegistryRow): string =>
          `${record.countryType.isoCode} / ${record.currencyType.isoCode}`,
      },
      {
        title: 'Items',
        dataIndex: 'itemCount',
        sorter: (a: TGiftRegistryRow, b: TGiftRegistryRow): number =>
          a.itemCount - b.itemCount,
      },
      {
        title: 'Expires',
        dataIndex: 'expiresAt',
        sorter: (a: TGiftRegistryRow, b: TGiftRegistryRow): number =>
          dayjs(a.expiresAt).unix() - dayjs(b.expiresAt).unix(),
        render: (expiresAt: Date): string =>
          dayjs(expiresAt).format(EDateFormat.DATE),
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        sorter: (a: TGiftRegistryRow, b: TGiftRegistryRow): number =>
          dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        render: (createdAt: Date): string =>
          dayjs(createdAt).format(EDateFormat.DATE),
      },
      {
        title: 'Actions',
        render: (
          _text: unknown,
          record: TGiftRegistryRow,
        ): React.ReactElement => (
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={(): void => setSelectedRegistryUUID(record.uuid)}
          >
            View details
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Gift registries"
        onReload={(): void => {
          dispatch(giftRegistriesLoad());
        }}
        isReloadDisabled={inProcess}
      />

      <UpdatedAt value={updatedAt} />

      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={dataSource}
        isLoading={inProcess && !registries}
        emptyText="No gift registries found"
      />

      <GiftRegistryDetailDrawer
        open={!!selectedRegistryUUID}
        onClose={(): void => setSelectedRegistryUUID(null)}
        registryUUID={selectedRegistryUUID}
      />
    </Space>
  );
}

export default GiftRegistriesContainer;
